import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAdminEntity from '../../../hooks/useAdminEntity';
import {
  getAmenitiesEndpoint,
  getAmenityDetailsEndpoint,
  uploadAmenityImageEndpoint,
} from '../services/projectService';
import { getInvestorProjectDetailEndpoint } from '../services/investorService';
import { InvestorProjectDetail, AmenityImageResponse } from '../models/investors';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';

export const useAmenityImageUpload = () => {
  const { id: projectId = '' } = useParams();

  const { isLoading: isApiLoading, callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const [errorAlert, setErrorAlert] = useState<any>(null);
  const [sweetAlert, setSweetAlert] = useState(false);
  const [alertProps, setAlertProps] = useState<any>({});

  // States
  const [selectedAmenityForImage, setSelectedAmenityForImage] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const [projectAmenities, setProjectAmenities] = useState<
    { value: string; label: string }[]
  >([]);

  const [amenityImageStatus, setAmenityImageStatus] = useState<Record<string, boolean>>({});
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  const [showAmenityModal, setShowAmenityModal] = useState(false);
  const [amenityImage, setAmenityImage] = useState<string | null>(null);

  const globalAlertOptions = {
    success: false,
    warning: true,
    onConfirm: () => setErrorAlert(null),
    title: 'Error',
    text: errorAlert || '',
  };

  // Fetch Project Details
  const getInvestorProject = useCallback(
    () => getInvestorProjectDetailEndpoint(projectId, token),
    [projectId, token],
  );
  const [investorProject] = useAdminEntity<InvestorProjectDetail>(
    getInvestorProject,
    setErrorAlert,
  );

  // Fetch all global amenities
  const getAmenitiesModel = useCallback(() => getAmenitiesEndpoint(token), [token]);
  const [allAmenities] = useAdminEntity<any[]>(getAmenitiesModel, setErrorAlert);

  useEffect(() => {
    if (allAmenities && allAmenities.length > 0 && investorProject?.amenities) {
      const current = allAmenities
        .filter((amenity) => investorProject.amenities.includes(amenity.id))
        .map((amenity) => ({
          value: amenity.id,
          label: amenity.description,
        }));
      setProjectAmenities(current);
    }
  }, [allAmenities, investorProject]);

  useEffect(() => {
    const fetchStatuses = async () => {
      if (projectAmenities.length === 0) return;
      setLoadingStatuses(true);
      const newStatuses: Record<string, boolean> = {};

      await Promise.all(
        projectAmenities.map((amenity) =>
          callEndpoint(
            getAmenityDetailsEndpoint(projectId, amenity.value, token),
          ).then(({ status, data }) => {
            if (status === 200) {
              const response = fetchResponseAdapter(data) as { data: AmenityImageResponse };
              newStatuses[amenity.value] = !!response.data?.image;
            } else {
              newStatuses[amenity.value] = false;
            }
          }).catch(() => {
            newStatuses[amenity.value] = false;
          }),
        ),
      );

      setAmenityImageStatus(newStatuses);
      setLoadingStatuses(false);
    };

    fetchStatuses();
  }, [projectAmenities, projectId, token, callEndpoint]);

  const fetchAmenityImage = useCallback(
    async (amenityId: string) => {
      const { status, data } = await callEndpoint(
        getAmenityDetailsEndpoint(projectId, amenityId, token),
      );
      if (status === 200) {
        const response = fetchResponseAdapter(data) as { data: AmenityImageResponse };
        setAmenityImage(response.data?.image || null);
      } else {
        setAmenityImage(null);
      }
    },
    [projectId, callEndpoint, token],
  );

  // Upload the file
  const uploadAmenityImage = useCallback(
    async (files: Array<any>) => {
      if (files.length < 1) return;

      const formData = new FormData();
      files.forEach((file) => formData.append('image', file));

      const { status, data } = await callEndpoint(
        uploadAmenityImageEndpoint(
          projectId,
          selectedAmenityForImage?.value || '',
          formData,
          token,
        ),
      );

      if (status === 200) {
        const response = fetchResponseAdapter(data) as { data: AmenityImageResponse };
        setAmenityImage(response.data?.image || null);

        if (selectedAmenityForImage?.value) {
          setAmenityImageStatus((prev) => ({
            ...prev,
            [selectedAmenityForImage.value]: true,
          }));
        }

        setShowAmenityModal(false);
        setAlertProps({
          success: true,
          title: 'Imagen guardada',
          text: 'La imagen ha sido guardada correctamente.',
          onConfirm: () => setSweetAlert(false),
        });
        setSweetAlert(true);
      } else {
        setAlertProps({
          danger: true,
          title: 'Error',
          text: 'Ocurrió un problema al subir la imagen. Por favor, intenta de nuevo.',
          onConfirm: () => setSweetAlert(false),
        });
        setSweetAlert(true);
      }
    },
    [projectId, selectedAmenityForImage, callEndpoint, token],
  );

  const handleOpenAmenityModal = useCallback(
    async (amenity: { value: string; label: string }) => {
      setSelectedAmenityForImage(amenity);
      await fetchAmenityImage(amenity.value);
      setShowAmenityModal(true);
    },
    [fetchAmenityImage],
  );

  const handleCloseAmenityModal = useCallback(() => {
    setShowAmenityModal(false);
    setAmenityImage(null);
  }, []);

  const isLoading = !investorProject || !allAmenities || (isApiLoading && projectAmenities.length === 0);

  return {
    isLoading,
    sweetAlert,
    alertProps,
    projectAmenities,
    loadingStatuses,
    amenityImageStatus,
    handleOpenAmenityModal,
    handleCloseAmenityModal,
    showAmenityModal,
    selectedAmenityForImage,
    uploadAmenityImage,
    amenityImage,
    globalAlertOptions,
  };
};
